import { NgForOf } from '@angular/common';
import { AfterViewInit, Component, inject, signal, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { BaseComponent } from '../../shared/base.component';
import { ViewEmployeesState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { Employee } from '../shared/employee.model';
import { EmployeeService } from '../shared/employee.service';

@Component({
    selector: 'app-view-employees',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatExpansionModule,
        MatTableModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        MatTooltip,
        NgForOf,
        SiteHeaderComponent
    ],
    templateUrl: './view-employees.component.html',
    styleUrl: './view-employees.component.css'
})
export class ViewEmployeesComponent extends BaseComponent implements AfterViewInit {

    private employeeService: EmployeeService = inject(EmployeeService);

    private static _retrievingEmployees = "Retrieving Employees";
    private static _retrievingEmployeesError = `${ViewEmployeesComponent._retrievingEmployees} error:`;
    private static _inactivatingEmployee = "Inactivating Employee";
    private static _inactivatingEmployeeError = `${ViewEmployeesComponent._inactivatingEmployee} error:`;
    private static _reactivatingEmployee = "Reactivating Employee";
    private static _reactivatingEmployeeError = `${ViewEmployeesComponent._reactivatingEmployee} error:`;
    private static _deletingEmployee = "Deleting Employee";
    private static _deletingEmployeeError = `${ViewEmployeesComponent._deletingEmployee} error:`;

    protected viewEmployeesForm = new FormGroup({
        lastName: new FormControl(""),
        firstName: new FormControl(""),
        employeeId: new FormControl("")
    });

    private _employees: WritableSignal<Employee[]> = signal<Employee[]>([]);
    private _employeeCount: WritableSignal<number> = signal<number>(-1);

    public constructor() {
        super();
    }

    public ngAfterViewInit() {
        const state: ViewEmployeesState | undefined = this.stateManager.viewEmployeesState;

        if (state != undefined) {
            this.viewEmployeesForm = state.viewEmployeesForm;

            if (this.refresh) {
                this.pageIndex = 0;
                this.employeeCount = -1;
                this.listEmployees(this.overrideSnackBar);
            } else {
                this.employees = state.employees;
                this.employeeCount = state.employeeCount;
                super.restoreState(state.baseComponentState);
            }
        }
    }

    public ngOnDestroy() {
        const state: ViewEmployeesState = {} as ViewEmployeesState;
        state.viewEmployeesForm = this.viewEmployeesForm;
        state.employees = this.employees;
        state.employeeCount = this._employeeCount();
        state.baseComponentState = super.getState();
        this.stateManager.viewEmployeesState = state;
    }

    public getAltCssClass(className: string): string {
        return (this.userType != "employee") ? className + "-alt" : "";
    }

    protected get employees(): Employee[] {
        return this._employees();
    }

    protected get employeeCount(): number {
        return this._employeeCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        super.handlePageEvent(e);
        this.listEmployees();
    }

    protected async listEmployees(): Promise<void>;
    protected async listEmployees(overrideSnackBar: string | undefined): Promise<void>;
    protected async listEmployees(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this._employeeCount.set(-1);
                this.markSearchParams(super.pristine);
            }

            const observable = await this.employeeService.getEmployees(await getIdToken(currentUser),
                this.getSearchParams(), this.pageSize, this.pageIndex);

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewEmployeesComponent._retrievingEmployees}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-employees"] == "success") {
                        // this._employees is a signal. The view will be refreshed
                        // upon update to the signal.
                        this.employees = Employee.toEmployees(json["employees"]);
                        this.employeeCount = json["employee-count"];
                        super.message = "";
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewEmployeesComponent._retrievingEmployeesError} ${(<Error>error).message}`;
                    console.error(message);
                    super.message = message;
                }
            })
        } else {
            const message = `${ViewEmployeesComponent._retrievingEmployeesError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        this.lastNameCtl.setValue("");
        this.firstNameCtl.setValue("");
        this.employeeIdCtl.setValue("");
        this.markSearchParams(super.dirty);
        this.listEmployees();
    }

    protected async addEmployee() {
        this.stateManager.addEmployeeState = undefined;
        this.router.navigate(["/add-employee"]);
    }

    protected async updateEmployee(id: number) {
        this.stateManager.updateEmployeeState = undefined;
        this.router.navigate([`/update-employee/${id}`]);
    }

    protected async inactivateEmployee(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.employeeService.inactivateEmployee(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewEmployeesComponent._inactivatingEmployee}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["inactivate-employee"] == "success") {
                        this.listEmployees("Employee inactivated");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewEmployeesComponent._inactivatingEmployeeError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30)
                }
            })
        } else {
            const message = `${ViewEmployeesComponent._inactivatingEmployeeError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async reactivateEmployee(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.employeeService.reactivateEmployee(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewEmployeesComponent._reactivatingEmployee}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["reactivate-employee"] == "success") {
                        this.listEmployees("Employee Reactivated");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewEmployeesComponent._reactivatingEmployeeError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30)
                }
            })
        } else {
            const message = `${ViewEmployeesComponent._reactivatingEmployeeError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async deleteEmployee(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.employeeService.deleteEmployee(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewEmployeesComponent._deletingEmployee}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["delete-employee"] == "success") {
                        if (this.employees.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }

                        this.listEmployees("Employee deleted");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewEmployeesComponent._deletingEmployeeError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewEmployeesComponent._deletingEmployeeError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        return this.lastNameCtl.dirty
            || this.firstNameCtl.dirty
            || this.employeeIdCtl.dirty;
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.lastNameCtl, func);
        super.markControl(this.firstNameCtl, func);
        super.markControl(this.employeeIdCtl, func);
    }

    private getSearchParams(): any {
        return {
            "lastName": this.lastNameCtl.value,
            "firstName": this.firstNameCtl.value,
            "employeeId": this.employeeIdCtl.value
        }
    }

    private get lastNameCtl(): FormControl {
        return this.viewEmployeesForm.controls.lastName;
    }

    private get firstNameCtl(): FormControl {
        return this.viewEmployeesForm.controls.firstName;
    }

    private get employeeIdCtl(): FormControl {
        return this.viewEmployeesForm.controls.employeeId;
    }

    private set employees(value: Employee[]) {
        this._employees.set(value);
    }

    private set employeeCount(value: number) {
        this._employeeCount.set(value);
    }
}
